import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Formik, Form } from 'formik';
import { Redirect } from 'react-router-dom';

import getListingFormFields from 'common/listingFormFields';
import setNullEmptyValues from 'common/util/setNullEmptyValues';

import { getListingFullAddressString } from 'common/listingHelpers';

import { formatPercentage } from 'common/formatters';

import {
  createInitialValues,
  createFormValidationFn,
  createFormFieldsByIdGetter,
  hideFieldIf,
} from 'client/helpers/formikHelpers';

import {
  dataTable,
  actionDataTableLoadItems,
  actionDataTableLoadItemsIfNotFullyLoaded,
  actionInitializeDataTableState,
} from 'client/helpers/dataTables';

import { SCREEN_ID_PARTNER_LISTING_EDIT_MOVE_IN } from 'client/constants/screenIds';

import { SR_API_ACTION_PARTNER_EDIT_LISTING } from 'api/actionTypes';

import { DATA_TABLE_SOURCE_PARTNER_LISTINGS } from 'common/dataTableConstants';

import {
  USER_ROLE_ADMIN,
  USER_ROLE_PARTNER,
  USER_ROLE_LANDLORD,
  hasRoleLandlord,
} from 'common/userRoles';

import { LEASE_TEMPLATE_TYPE_CUSTOM_LEASE } from 'common/financeConstants';

import SrBox from 'client/components/SrBox';
import SrText from 'client/components/SrText';
import SrFlexRow from 'client/components/SrFlexRow/SrFlexRow';
import SrCard, {
  SR_CARD_BULLET_COLOR_BLACK,
  SR_CARD_CONTENT_TYPE_FORM_REGULAR,
} from 'client/components/SrCard/SrCard';
import SrFormikField, { SR_FIELD_THEME_ADOBE } from 'client/components/SrFormikField';
import SrStaticFooter from 'client/components/SrStaticFooter';
import SrFooterButton from 'client/components/SrFooterButton/SrFooterButton';
import SrGridRow from 'client/components/SrGridRow/SrGridRow';
import SrPartnerPageV2 from 'client/components/SrPartnerPageV2/SrPartnerPageV2';
import SrAccountManagerContactSupport from 'client/components/SrAccountManagerContactSupport/SrAccountManagerContactSupport';
import SrSimpleMessageModal from 'client/components/SrSimpleMessageModal/SrSimpleMessageModal';
import SrRouteLeavingGuard from 'client/components/SrRouteLeavingGuard';

import PartnerActions from 'client/actions/Partner';
import AccountManagerActions from 'client/actions/AccountManager';
import postWithState from 'client/actions/postWithState';
import withApiState from 'client/helpers/withApiState';
import withTransitionHook from 'client/helpers/withTransitionHook';
import withScreenId from 'client/helpers/withScreenId';
import withScrollToTop from 'client/helpers/withScrollToTop';
import allowedRoles, { getUserLandingPage } from 'client/helpers/allowedRoles';
import branch from 'client/core/branch';
import SrInfoTile from 'client/components/SrInfoTile/SrInfoTile';

const FormikContext = React.createContext();

function getFormFields({ values = {}, listing }) {
  let formFields = getListingFormFields({
    fieldgroup: [
      'leaseTemplateType',
      'customLeaseTemplateUrl',
      'minLeaseMonths',
      'maxLeaseMonths',
      'rentPaymentRecipientName',
      'rentPaymentRecipientAddress',
      'rentPaymentMethods',
      'rentPaymentOtherMethodDescription',
      'isOwnerManaged',
      'propertyManagerType',
      'propertyManagerName',
      'propertyManagerPhone',
      'propertyManagerEmail',
      'invoiceRecipientName',
      'invoiceRecipientEmail',
      'invoiceRecipientPhone',
    ],
  });

  formFields = formFields.map((formField) => {
    if (formField.id === 'customLeaseTemplateUrl') {
      const listingAddress = getListingFullAddressString(listing);
      return hideFieldIf(values.leaseTemplateType !== LEASE_TEMPLATE_TYPE_CUSTOM_LEASE, {
        ...formField,
        properties: {
          ...formField.properties,
          filePath: `/listing-uploads/lease-templates/Custom Lease - ${listingAddress}`,
        },
      });
    }
    return formField;
  });

  return formFields;
}

function getListingDataTableSettings({ partner, match }) {
  const listingId = parseInt(match.params.listingId, 10);
  if (!partner) {
    return null;
  }
  const dataTableSettings = {
    itemsPropName: 'listing',
    dataSource: DATA_TABLE_SOURCE_PARTNER_LISTINGS,
    viewId: `by-listingId-${listingId}`,
    count: 1,
    injectSingleItem: true,
    filter: {
      'listings.id': parseInt(listingId, 10),
      'listings.partnerId': partner.id,
    },
    customParams: {
      partnerListings: {},
    },
  };

  return dataTableSettings;
}

@withTransitionHook
@allowedRoles({
  roles: [USER_ROLE_ADMIN, USER_ROLE_PARTNER, USER_ROLE_LANDLORD],
  redirectUrl: '/login',
  addRedirectQueryParam: true,
})
@withScreenId(SCREEN_ID_PARTNER_LISTING_EDIT_MOVE_IN)
@withScrollToTop
@withApiState({
  partnerEditListingApiState: {
    action: SR_API_ACTION_PARTNER_EDIT_LISTING,
  },
})
@branch(({ match }) => ({
  partner: ['partners', 'bySlug', match.params.partnerSlug],
  currentUser: ['currentUser'],
  accountManager: ['accountManagers', 'byListingId', match.params.listingId],
  listingStripeAccount: ['listingStripeAccountsByListingId', match.params.listingId],
}))
@dataTable(getListingDataTableSettings)
class PartnerEditListingMoveInPage extends Component {
  static getPageMetadata = () => ({
    pageTitle: 'Edit Listing | Move-In Process',
  });

  static fetchData(match, inject) {
    const partner = inject.state.get(['partners', 'bySlug', match.params.partnerSlug]);
    const listingId = parseInt(match.params.listingId, 10);

    const promises = [
      AccountManagerActions.getByListingId(inject, { listingId }),
      PartnerActions.loadListingStripeAccount(inject, { listingId }),
    ];

    if (!partner) {
      promises.push(
        PartnerActions.loadPartnerBySlug(inject, {
          slug: match.params.partnerSlug,
        }),
      );
    }

    const listingDataTableSettings = getListingDataTableSettings({ partner, match });
    if (listingDataTableSettings) {
      actionInitializeDataTableState(inject, listingDataTableSettings);
      promises.push(actionDataTableLoadItemsIfNotFullyLoaded(inject, listingDataTableSettings));
    }

    return Promise.all(promises);
  }

  static propTypes = {
    partner: PropTypes.object,
    listing: PropTypes.object,
    accountManager: PropTypes.object,
    isTransitioning: PropTypes.bool.isRequired,
    partnerEditListingApiState: PropTypes.object.isRequired,
  };

  constructor(props) {
    super(props);
    this.state = {
      editSuccessModalIsOpen: false,
    };
  }

  componentDidMount() {
    if (process.browser) {
      global.addEventListener('beforeunload', this._onBeforeUnload, true);
    }
  }

  componentWillUnmount() {
    if (process.browser) {
      global.removeEventListener('beforeunload', this._onBeforeUnload, true);
    }
  }

  _onBeforeUnload = (event) => {
    if (this.dirty) {
      event.preventDefault();
      event.returnValue = '';
    }
  };

  _onSubmit = (formValues) => {
    const { listing, partner, dispatch } = this.props;
    const updates = setNullEmptyValues(formValues);

    if (formValues.leaseTemplateType !== LEASE_TEMPLATE_TYPE_CUSTOM_LEASE) {
      formValues.customLeaseTemplateUrl = null;
    }

    dispatch(postWithState, {
      action: SR_API_ACTION_PARTNER_EDIT_LISTING,
      payload: {
        partnerId: partner.id,
        listingId: listing.id,
        updates: updates,
      },
      onSuccess: () => {
        this.setState({
          editSuccessModalIsOpen: true,
        });
        this._reloadPartnerListing();
      },
    });
  };

  _reloadPartnerListing = () => {
    const { partner, match, dispatch } = this.props;

    return Promise.all([
      dispatch(
        actionDataTableLoadItems,
        getListingDataTableSettings({
          partner,
          match,
        }),
      ),
    ]);
  };

  _onCloseEditSuccessModal = () => {
    this.setState({
      editSuccessModalIsOpen: false,
    });
  };

  render() {
    const {
      listing,
      partner,
      currentUser,
      accountManager,
      isTransitioning,
      partnerEditListingApiState,
      dispatch,
    } = this.props;
    const { editSuccessModalIsOpen } = this.state;

    const isLandlord =
      hasRoleLandlord(currentUser.roles) && listing?.landlordUserId === currentUser.id;

    if (isTransitioning && (!partner || !listing)) {
      return null;
    }

    if (!partner || !listing) {
      const userLandingPage = getUserLandingPage({
        userRoles: currentUser.roles,
      });
      return <Redirect to={userLandingPage} />;
    }

    return (
      <div className="PartnerEditListingMoveInPage">
        <SrPartnerPageV2
          backUrl={`/partner/${partner.slug}/listing/${listing.id}/edit`}
          navbarTitle={listing ? getListingFullAddressString(listing) : null}
          pageHeader="Lease &amp; Move-In"
        >
          <Formik
            ref={(formik) => {
              this.formik = formik;
            }}
            initialValues={createInitialValues({
              formFields: getFormFields({ listing }),
              defaultValues: {
                ...listing,
              },
            })}
            enableReinitialize
            validate={(values) => {
              const formFields = getFormFields({ values, listing });
              return createFormValidationFn(formFields)(values);
            }}
            onSubmit={this._onSubmit}
            render={(formikBag) => {
              const formFields = getFormFields({ values: formikBag.values, listing });
              const getFormFieldsById = createFormFieldsByIdGetter(formFields);
              const customLeaseTemplateUrlField = getFormFieldsById('customLeaseTemplateUrl');
              this.dirty = formikBag.dirty;

              return (
                <>
                  <FormikContext.Provider value={formikBag}>
                    <Form>
                      <SrCard
                        color={SR_CARD_BULLET_COLOR_BLACK}
                        contentType={SR_CARD_CONTENT_TYPE_FORM_REGULAR}
                        header="Lease Contract"
                        subheader={
                          'Sunroom drafts the lease using the template you select below. ' +
                          'Signatures are collected digitally. Renters sign first, then owners.'
                        }
                      >
                        <>
                          <SrFormikField
                            formField={getFormFieldsById('leaseTemplateType')}
                            formikBag={formikBag}
                            theme={SR_FIELD_THEME_ADOBE}
                          />
                          {customLeaseTemplateUrlField.type !== 'hidden' && (
                            <SrGridRow autofit autofitMinColumnWidth="405px">
                              <SrFormikField
                                formField={customLeaseTemplateUrlField}
                                formikBag={formikBag}
                                theme={SR_FIELD_THEME_ADOBE}
                              />
                            </SrGridRow>
                          )}
                          <SrBox marginTop="23px">
                            <SrText
                              display="block"
                              lineHeight="25px"
                              font="greycliff"
                              color="blueGreycliff"
                              fontSize="21px"
                              weight="800"
                              paddingBottom="8px"
                            >
                              Addendums
                            </SrText>
                            <SrText
                              lineHeight="25px"
                              font="greycliff"
                              fontSize="16px"
                              weight="500"
                              color="grey600"
                            >
                              Please email any addendums you want Sunroom to add the to lease.
                              <br />
                              Email us at
                              <SrText
                                fontSize="16px"
                                lineHeight="25px"
                                font="greycliff"
                                weight="500"
                              >
                                {' leasing@sunroomrentals.com'}
                              </SrText>
                            </SrText>
                          </SrBox>
                          <SrGridRow autofit autofitMinColumnWidth="185px">
                            <SrFormikField
                              formField={getFormFieldsById('minLeaseMonths')}
                              formikBag={formikBag}
                              theme={SR_FIELD_THEME_ADOBE}
                            />
                            <SrFormikField
                              formField={getFormFieldsById('maxLeaseMonths')}
                              formikBag={formikBag}
                              theme={SR_FIELD_THEME_ADOBE}
                            />
                          </SrGridRow>
                        </>
                      </SrCard>

                      <SrCard
                        color={SR_CARD_BULLET_COLOR_BLACK}
                        contentType={SR_CARD_CONTENT_TYPE_FORM_REGULAR}
                        header="Rent Payments"
                        subheader="Sunroom drafts the lease using the information you enter below."
                      >
                        <div>
                          <SrFlexRow>
                            <SrFormikField
                              formField={getFormFieldsById('rentPaymentRecipientName')}
                              formikBag={formikBag}
                              theme={SR_FIELD_THEME_ADOBE}
                            />
                            <SrFormikField
                              formField={getFormFieldsById('rentPaymentRecipientAddress')}
                              formikBag={formikBag}
                              theme={SR_FIELD_THEME_ADOBE}
                            />
                          </SrFlexRow>
                          <SrGridRow autofit autofitMinColumnWidth="300px">
                            <SrFormikField
                              formField={getFormFieldsById('rentPaymentMethods')}
                              formikBag={formikBag}
                              theme={SR_FIELD_THEME_ADOBE}
                            />
                          </SrGridRow>
                          <SrGridRow autofit autofitMinColumnWidth="405px">
                            <SrFormikField
                              formField={getFormFieldsById('rentPaymentOtherMethodDescription')}
                              formikBag={formikBag}
                              theme={SR_FIELD_THEME_ADOBE}
                            />
                          </SrGridRow>
                        </div>
                      </SrCard>

                      <SrCard
                        color={SR_CARD_BULLET_COLOR_BLACK}
                        contentType={SR_CARD_CONTENT_TYPE_FORM_REGULAR}
                        header="Property Manager / Maintenance Contact"
                        subheader={
                          'After the lease is signed, Sunroom connects the renters with the ' +
                          'property manager/landlord to coordinate the move in process.'
                        }
                      >
                        <div>
                          <SrGridRow autofit autofitMinColumnWidth="185px">
                            <SrFormikField
                              formField={getFormFieldsById('isOwnerManaged')}
                              formikBag={formikBag}
                              theme={SR_FIELD_THEME_ADOBE}
                            />
                          </SrGridRow>
                          <SrFormikField
                            formField={getFormFieldsById('propertyManagerType')}
                            formikBag={formikBag}
                            theme={SR_FIELD_THEME_ADOBE}
                          />
                          <SrGridRow autofit autofitMinColumnWidth="185px">
                            <SrFormikField
                              formField={getFormFieldsById('propertyManagerName')}
                              formikBag={formikBag}
                              theme={SR_FIELD_THEME_ADOBE}
                            />
                            <SrFormikField
                              formField={getFormFieldsById('propertyManagerPhone')}
                              formikBag={formikBag}
                              theme={SR_FIELD_THEME_ADOBE}
                            />
                            <SrFormikField
                              formField={getFormFieldsById('propertyManagerEmail')}
                              formikBag={formikBag}
                              theme={SR_FIELD_THEME_ADOBE}
                            />
                          </SrGridRow>
                        </div>
                      </SrCard>

                      <SrCard
                        color={SR_CARD_BULLET_COLOR_BLACK}
                        contentType={SR_CARD_CONTENT_TYPE_FORM_REGULAR}
                        header="Billing"
                        subheader="Upon lease sign, our accounting team will invoice your brokerage."
                      >
                        <div>
                          <SrGridRow autofit autofitMinColumnWidth="185px">
                            <SrFormikField
                              formField={getFormFieldsById('invoiceRecipientName')}
                              formikBag={formikBag}
                              theme={SR_FIELD_THEME_ADOBE}
                            />
                            <SrFormikField
                              formField={getFormFieldsById('invoiceRecipientEmail')}
                              formikBag={formikBag}
                              theme={SR_FIELD_THEME_ADOBE}
                            />
                            <SrFormikField
                              formField={getFormFieldsById('invoiceRecipientPhone')}
                              formikBag={formikBag}
                              theme={SR_FIELD_THEME_ADOBE}
                            />
                          </SrGridRow>
                          {partner.billingFees.commissionPercentage && !isLandlord && (
                            <SrBox marginTop="32px">
                              <SrInfoTile
                                theme="adobe"
                                label="Payment"
                                value={`${formatPercentage(
                                  partner?.billingFees?.leaseFeePercentage,
                                )}
                                  of first month's rent`}
                              />
                            </SrBox>
                          )}
                        </div>
                      </SrCard>

                      {accountManager && (
                        <SrAccountManagerContactSupport accountManager={accountManager} />
                      )}

                      <SrStaticFooter>
                        <SrText
                          display="block"
                          lineHeight="25px"
                          font="greycliff"
                          color="grey600"
                          fontSize="13px"
                          weight="500"
                        >
                          Updates can take 24 hours to syndicate
                        </SrText>
                        <SrFooterButton
                          hasBoxShadow={!partnerEditListingApiState.inProgress}
                          disabled={partnerEditListingApiState.inProgress}
                        >
                          {partnerEditListingApiState.inProgress ? 'Saving...' : 'Save'}
                        </SrFooterButton>
                      </SrStaticFooter>
                    </Form>
                  </FormikContext.Provider>
                  <SrRouteLeavingGuard when={formikBag.dirty} dispatch={dispatch} />
                </>
              );
            }}
          />
        </SrPartnerPageV2>
        {editSuccessModalIsOpen && (
          <SrSimpleMessageModal
            iconUrl="/images/checkmarkSuccessGreen.svg"
            headline={
              <span>
                Successfully <br /> edited property!
              </span>
            }
            message="Changes are live instantly on Sunroom."
            buttonText="ok, thanks"
            maxWidth={375}
            onClose={this._onCloseEditSuccessModal}
          />
        )}
      </div>
    );
  }
}

PartnerEditListingMoveInPage.displayName = 'PartnerEditListingMoveInPage';

export default PartnerEditListingMoveInPage;
